import { useActiveAddress, useConnection } from "@arweave-wallet-kit/react"
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents"
import {
  Box,
  Grid,
  Stack,
  Typography,
  Chip,
  Divider,
  TableRow,
  TableCell,
  Theme,
  useMediaQuery,
  Tooltip,
  Button,
} from "@mui/material"
import { dryrun } from "@permaweb/aoconnect"
import { HelpCircle } from "@untitled-ui/icons-react"
import { motion } from "framer-motion"
import numbro from "numbro"
import { useEffect, useMemo, useState } from "react"

import { HeaderCell, InMemoryTable } from "../components/InMemoryTable"
import { PageWrapper } from "../components/PageWrapper"
import { TitleFont } from "../theme"
import { shortHash } from "../utils/hash"

const processId = "x-bnBoJ7kv3DJ6UziJnQWF9bJ3hhJDDMRms2ZqwJD_I"

// Add a status enum for better type safety and flexibility
type CampaignStatus = "active" | "upcoming" | "finished" | "coming-soon"

type Campaign = {
  index: number
  id: string
  title: string
  description: string
  tooltip?: string
  requirement?: string
  status: CampaignStatus // Add status field
}

type PointsData = {
  campaigns: {
    liquidity: CampaignData
    volume: CampaignData
    balance: CampaignData
  }
  weekly_records: WeeklyRecord[]
  total_points: number
}

type CampaignData = {
  updates: CampaignUpdate[]
  is_continuous: boolean
  total_points: number
  current_tier: string
}

type CampaignUpdate = {
  date: string
  tier: string
  points: number
}

type WeeklyRecord = {
  date: string
  tier: string
  campaign_type: string
  points: number
}

// dummy

const campaigns: Campaign[] = [
  {
    index: 0,
    id: "volume",
    title: "Volume Conqueror",
    description: "Trade on the Botega AMM to accumulate USD volume and earn points.",
    tooltip: "Any swap routed through Botega pools—whether via Dexi or trading bots—earns points.",
    requirement:
      "Generate trading volume to earn points. Botega takes regular onchain spanshots, this is a rolling campaign.",
    status: "active",
  },
  {
    index: 1,
    id: "liquidity",
    title: "Liquidity Builder",
    description: "Provide liquidity in Botega pools and earn.",
    tooltip:
      "The points are distributed based on LH (Liquidity Hours) which is the amount of time you've had liquidity in the pool multiplied by the USD value.",
    requirement:
      "Maintain LP tokens for multiple days in eligible pools; points scale with duration.",
    status: "active",
  },
  {
    index: 2,
    id: "balance",
    title: "$AGENT Holder Part I",
    description: "Hold $AGENT tokens to earn points Phase I",
    tooltip: "Hold $AGENT tokens to earn points. Just hold the token...",
    requirement: "Hold $AGENT tokens to earn points.",
    status: "finished",
  },
  {
    index: 3,
    id: "balance_2",
    title: "$AGENT Holder Part II",
    description: "Hold $AGENT tokens to earn points in phase 2.",
    tooltip: "Hold $AGENT tokens to earn points in the second phase of the campaign.",
    requirement: "Hold $AGENT tokens to earn points.",
    status: "upcoming",
  },
]

// Update the helper function at the top level
const getLatestWeekAchievements = (weeklyRecords: WeeklyRecord[] = []) => {
  if (weeklyRecords.length === 0) return []

  // Sort records by date descending to get the most recent first
  const sortedRecords = [...weeklyRecords].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  )

  // Get the most recent date
  const latestDate = sortedRecords[0].date

  // Filter records from the latest week only and with points > 0
  return sortedRecords.filter((record) => record.date === latestDate && record.points > 0)
}

export default function PointsFarming() {
  useEffect(() => {
    document.title = "Rewards Hub · Botega"
  }, [])

  const connectedWallet = useActiveAddress() as string
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"))
  const { connect } = useConnection()

  const [pointsData, setPointsData] = useState<PointsData | null>(null)

  useEffect(() => {
    const fetchPoints = async () => {
      if (!connectedWallet) return

      try {
        const result = await dryrun({
          process: processId,
          tags: [
            { name: "Action", value: "Get-Points" },
            { name: "User", value: connectedWallet },
          ],
        })

        if (result.Messages.length === 0) {
          // If no messages, set default empty state
          setPointsData({
            campaigns: {
              liquidity: { updates: [], is_continuous: true, total_points: 0, current_tier: "" },
              volume: { updates: [], is_continuous: true, total_points: 0, current_tier: "" },
              balance: { updates: [], is_continuous: true, total_points: 0, current_tier: "" },
            },
            weekly_records: [],
            total_points: 0,
          })
          return
        }

        // Handle empty array response
        const responseData = result.Messages[0].Data
        if (responseData === "[]") {
          setPointsData({
            campaigns: {
              liquidity: { updates: [], is_continuous: true, total_points: 0, current_tier: "" },
              volume: { updates: [], is_continuous: true, total_points: 0, current_tier: "" },
              balance: { updates: [], is_continuous: true, total_points: 0, current_tier: "" },
            },
            weekly_records: [],
            total_points: 0,
          })
          return
        }

        const data = JSON.parse(responseData) as PointsData
        setPointsData(data)
      } catch (error) {
        console.error("Error fetching points:", error)
        // Set default state on error
        setPointsData({
          campaigns: {
            liquidity: { updates: [], is_continuous: true, total_points: 0, current_tier: "" },
            volume: { updates: [], is_continuous: true, total_points: 0, current_tier: "" },
            balance: { updates: [], is_continuous: true, total_points: 0, current_tier: "" },
          },
          weekly_records: [],
          total_points: 0,
        })
      }
    }

    fetchPoints()
  }, [connectedWallet])

  // Update the placeholder points with actual data
  const totalPoints = pointsData?.total_points ?? 0

  // Calculate 30d points (sum of all weekly records in last 30 days)
  const points30d = useMemo(() => {
    if (!pointsData?.weekly_records) return 0

    const thirtyDaysAgo = new Date()
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)

    return pointsData.weekly_records
      .filter((record) => new Date(record.date) >= thirtyDaysAgo)
      .reduce((sum, record) => sum + record.points, 0)
  }, [pointsData?.weekly_records])

  const headerCells: HeaderCell[] = [
    { label: "#" },
    { label: "Campaign" },
    { label: "Active" },
    { label: "Description" },
    {
      label: (
        <Tooltip
          title={
            "The total points you've earned so far in this campaign, your results will be visible on Feb 15."
          }
        >
          <span>
            Points Earned{" "}
            <HelpCircle style={{ height: "1rem", width: "1rem", verticalAlign: "text-top" }} />
          </span>
        </Tooltip>
      ),
      align: "right",
    },
  ]

  const CampaignsTableRow = ({ campaign, index }: { campaign: Campaign; index: number }) => {
    const campaignPoints =
      pointsData?.campaigns[campaign.id as keyof typeof pointsData.campaigns]?.total_points ?? 0

    // Helper function to get status styling
    const getStatusStyle = (status: CampaignStatus) => {
      switch (status) {
        case "active":
          return {
            color: "#44b700",
            backgroundColor: "#44b700",
            animation: "ripple 1.2s infinite ease-in-out",
          }
        case "upcoming":
          return {
            color: "#ffc107",
            backgroundColor: "#ffc107",
            animation: "ripple 1.2s infinite ease-in-out",
          }
        case "finished":
          return {
            color: "#9e9e9e",
            backgroundColor: "#9e9e9e",
            animation: "none",
          }
      }

      return {}
    }

    const statusStyle = getStatusStyle(campaign.status)

    return (
      <TableRow
        sx={{
          border: (theme) => `1px solid ${theme.palette.enhanced.main}`,
          filter: campaign.id === "coming-soon" ? "blur(4px)" : "",
          pointerEvents: campaign.id === "coming-soon" ? "none" : "auto",
          opacity: campaign.status === "coming-soon" ? 0.7 : 1,
        }}
      >
        <TableCell align="center">
          <Typography variant="body2" color="text.secondary">
            {index + 1}
          </Typography>
        </TableCell>
        <TableCell sx={{ minWidth: 200 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              variant="subtitle1"
              fontFamily={TitleFont}
              sx={{
                fontWeight: 600,
              }}
            >
              {campaign.title}
            </Typography>
          </Stack>
        </TableCell>
        <TableCell sx={{ minWidth: 130 }}>
          {campaign.id === "coming-soon" ? (
            <Typography variant="caption" color="text.secondary">
              Coming soon
            </Typography>
          ) : (
            <Stack alignItems="center" direction="row" gap={1}>
              <Box
                sx={{
                  position: "relative",
                  display: "inline-block",
                  width: "12px",
                  height: "12px",
                  borderRadius: "50%",
                  backgroundColor: statusStyle.backgroundColor,
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    width: "12px",
                    height: "12px",
                    borderRadius: "50%",
                    backgroundColor: statusStyle.backgroundColor,
                    animation: statusStyle.animation,
                  },
                  "@keyframes ripple": {
                    "0%": {
                      transform: "scale(.8)",
                      opacity: 1,
                    },
                    "100%": {
                      transform: "scale(2.4)",
                      opacity: 0,
                    },
                  },
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
              </Typography>
            </Stack>
          )}
        </TableCell>
        <TableCell>
          <Typography variant="body2" title={campaign.tooltip}>
            {campaign.description}{" "}
            {campaign.tooltip && (
              <Tooltip title={campaign.tooltip}>
                <span>
                  <HelpCircle
                    style={{ height: "1rem", width: "1rem", verticalAlign: "text-top" }}
                  />
                </span>
              </Tooltip>
            )}
          </Typography>
        </TableCell>
        <TableCell align="right" sx={{ minWidth: 160 }}>
          <Typography
            variant="body2"
            sx={campaign.status === "coming-soon" ? { filter: "blur(4px)" } : undefined}
          >
            {campaign.id === "coming-soon"
              ? 0
              : campaignPoints > 0
                ? numbro(campaignPoints).format({ thousandSeparated: true })
                : "-"}
          </Typography>
        </TableCell>
      </TableRow>
    )
  }

  const tableData = [
    ...campaigns,
    {
      index: 4,
      id: "coming-soon",
      title: "PnL Wizard",
      description:
        "Contrary to popular belief, Lorem Ipsum is not simply random text. It has roots in a piece of classical Latin literature from 45 BC.",
    },
    {
      index: 5,
      id: "coming-soon",
      title: "Agentic Ape",
      description:
        "The point of using Lorem Ipsum is that it has a more-or-less normal distribution of letters, as opposed to using 'Content here, content here', making it look like readable English. ",
    },
  ]

  return (
    <PageWrapper>
      <Stack sx={{ position: "relative" }}>
        <Typography
          variant="h6"
          fontWeight={700}
          sx={{
            backgroundColor: "var(--mui-palette-accent-main)",
            borderRadius: "100px",
            color: "white",
            marginBottom: 6,
            paddingX: 1.5,
            alignSelf: "center",
          }}
        >
          The Official Botega Points Campaign is LIVE!
        </Typography>
        <Box
          sx={(theme) => ({
            background: theme.palette.background.paper,
            borderRadius: "8px",
            padding: 3,
            border: `1px solid ${theme.palette.divider}`,
          })}
        >
          <Grid container spacing={3}>
            {/* Left: Points Dashboard */}
            <Grid item xs={12} md={6}>
              {connectedWallet && (
                <Stack direction="row" alignItems="center" spacing={1} sx={{ marginBottom: 3 }}>
                  <Box
                    sx={(theme) => ({
                      background: theme.palette.accent.main,
                      width: "6px",
                      height: "6px",
                    })}
                  />
                  <Typography
                    sx={{
                      fontSize: "1.5rem",
                      fontWeight: 600,
                      marginBottom: "1rem",
                    }}
                  >
                    {shortHash(connectedWallet)}
                  </Typography>
                  <Chip
                    label="Your Wallet"
                    size="small"
                    sx={(theme) => ({
                      background: theme.palette.accent.main,
                      color: theme.palette.common.white,
                      borderRadius: "4px",
                    })}
                  />
                </Stack>
              )}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle1">Total Points</Typography>
                  <Typography variant="h6">
                    {numbro(totalPoints).format({ thousandSeparated: true })}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle1">30-Day Points</Typography>
                  <Typography variant="h6">
                    {numbro(points30d).format({ thousandSeparated: true })}
                  </Typography>
                </Grid>
              </Grid>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 2, mr: 3, display: "block" }}
              >
                Botega Points unlock exclusive rewards. 50% of all Botega tokens will be distributed
                based on points accrued.
              </Typography>
            </Grid>
            {!isMobile && (
              <Divider
                orientation="vertical"
                flexItem
                sx={{ mr: "-1px", mt: "1rem", display: { xs: "none", md: "flex" } }}
              />
            )}
            <Grid item xs={12} md={6}>
              <Typography
                sx={{
                  fontSize: "1.25rem",
                  fontWeight: 400,
                  color: "text.secondary",
                }}
              >
                Your Latest Achievements
              </Typography>
              <Stack gap={0.75} marginTop={1.5}>
                {pointsData &&
                  getLatestWeekAchievements(pointsData.weekly_records).map((record) => {
                    const campaign = campaigns.find((c) => c.id === record.campaign_type)
                    if (!campaign) return null

                    return (
                      <Stack
                        key={`${record.date}-${record.campaign_type}`}
                        direction="row"
                        alignItems="center"
                        spacing={1}
                      >
                        <EmojiEventsIcon color="action" fontSize="small" />
                        <Typography variant="body1">
                          {campaign.title} (
                          {numbro(record.points).format({ thousandSeparated: true })} points) -{" "}
                          {record.tier}
                        </Typography>
                      </Stack>
                    )
                  })}
                {!pointsData && (
                  <Typography variant="body2" color="text.secondary">
                    Loading achievements...
                  </Typography>
                )}
                {pointsData &&
                  getLatestWeekAchievements(pointsData.weekly_records).length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      No achievements in the latest week. Keep participating to earn more points!
                    </Typography>
                  )}
              </Stack>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 2, mr: 3, display: "block" }}
              >
                Showing achievements from your most recent week of activity. Keep participating to
                earn more points!
              </Typography>
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Campaigns
          </Typography>
          <InMemoryTable
            initialSortDir="asc"
            initialSortField="index"
            data={tableData}
            headerCells={headerCells}
            renderRow={(campaign: Campaign, index: number) => (
              <CampaignsTableRow key={campaign.index} campaign={campaign} index={index} />
            )}
          />
        </Box>
        <Typography variant="body2" color="text.secondary" align="center" marginY={2}>
          <i>More ways to earn Botega Points are on the horizon. Check back often!</i>
        </Typography>
        <Box
          sx={{
            position: "absolute",
            top: -50,
            left: -50,
            right: -50,
            bottom: -50,
            backdropFilter: "blur(4px)",
            transition: "backdrop-filter 1s, opacity 1s",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(var(--mui-palette-background-defaultChannel) / 0.8)",
            p: 3,
            opacity: connectedWallet ? 0 : 1,
            pointerEvents: connectedWallet ? "none" : "auto",
          }}
        >
          <Stack spacing={2} alignItems="center" maxWidth={400}>
            <Typography variant="h6" align="center" lineHeight={1.1}>
              Connect your wallet to start earning <i>Botega Points</i> and become eligible for{" "}
              <i>Botega Token Rewards</i>.
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              Botega Points unlock exclusive rewards. 50% of all Botega tokens will be distributed
              based on points accrued.
            </Typography>
            <Button
              size="small"
              variant="contained"
              sx={{
                borderRadius: 5,
                background: "var(--mui-palette-background-paper)",
                minWidth: "unset",
                paddingX: { xs: 0.75, md: 2 },
              }}
              onClick={() => {
                connect()
              }}
            >
              Connect Wallet
            </Button>
          </Stack>
        </Box>
      </Stack>
    </PageWrapper>
  )
}

const CountdownTimer = () => {
  const [timeRemaining, setTimeRemaining] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  })

  useEffect(() => {
    const targetDate = new Date("2025-02-18T17:00:00Z") // Feb 15th
    const updateTimer = () => {
      const now = new Date()
      const difference = targetDate.getTime() - now.getTime()

      if (difference <= 0) {
        setTimeRemaining({ days: 0, hours: 0, minutes: 0, seconds: 0 })
        return
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24))
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60))
      const seconds = Math.floor((difference % (1000 * 60)) / 1000)

      setTimeRemaining({ days, hours, minutes, seconds })
    }

    const timerInterval = setInterval(updateTimer, 1000)
    updateTimer() // Initial call to set the time right away

    return () => clearInterval(timerInterval) // Cleanup on component unmount
  }, [])

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1 }}>
      <Typography variant="h4" align="center" sx={{ fontWeight: 600 }} fontFamily={TitleFont}>
        {timeRemaining.days}d {timeRemaining.hours}h {timeRemaining.minutes}m{" "}
        {timeRemaining.seconds}s
      </Typography>
    </motion.div>
  )
}
